import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {AllOffersOverlay} from 'components/Accommodation/Offers/AllOffersOverlay'
import {AvailabilityPopupWrapper} from 'components/AvailabilityCalendarPopup'
import CookieBanner from 'components/CookieBanner'
import {ExitPopupSearchPage} from 'components/ExitPopup'
import {AllFilters} from 'components/FiltersAll'
import LanguageMismatchOverlay from 'components/LanguageMismatchOverlay/LanguageMismatchOverlay'
import SpinnerImportLoader from 'components/Loaders/SpinnerImportLoader'
import {MapOverlay} from 'components/MapOverlay'
import Search from 'components/Search'
import SearchBoxShadow from 'components/SearchBoxShadow'
import {SearchLayoutContainer} from 'components/SearchLayoutContainer'
import {ShowMapFloatingButton} from 'components/ShowMapFloatingButton/ShowMapFloatingButton'
import {SortByMobile} from 'components/SortBy/SortByMobile'
import {OfferViewContext, OfferViewType} from 'context/offerViewContext'
import {useAutoRefreshStaleSearches} from 'hooks/useAutoRefreshStaleSearches'
import {useConditionalEffect} from 'hooks/useConditionalEffect'
import {useHandleUserGeolocation} from 'hooks/useHandleUserGeolocation'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {useRedirectToHomeIfGeolocationFails} from 'hooks/useRedirectToHomeIfGeolocationFails'
import {getAnchorHotelId, getModuleState} from 'modules/sapiSearch/selectors'
import {ModuleState} from 'modules/sapiSearch/slice'
import {useInitSearch} from 'modules/sapiSearch/useSapiSearch'
import dynamic from 'next/dynamic'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {InitializeCallToBook} from '../../components/CallToBook/InitializeCallToBook'
import {getSearchBoxValues} from '../../modules/searchBox/selectors'

const Map = dynamic(
  async () =>
    import(
      /* webpackChunkName: "Map" */
      'components/Map'
    ),
  {loading: SpinnerImportLoader, ssr: false}
)
Map.displayName = 'MapLoadable'

const SearchWrapper = styled.div`
  flex: 1 auto;
  display: flex;
  flex-direction: column;
`

const SearchView = () => {
  const {isMobile, isDesktop} = useDeviceLayout()
  const {search, retention} = useBrandCapabilities()
  const isSeoSRP = useIsSeoSRP()
  const toggle = useToggle()

  const moduleState = useSelector(getModuleState)
  const searchStateDataId = useMemo(() => {
    switch (moduleState) {
      case ModuleState.SearchCompleted:
        return 'SearchCompleted'
      default:
        return 'SearchIdle'
    }
  }, [moduleState])

  const {hotelId} = useSelector(getSearchBoxValues)
  const anchorHotelId = useSelector(getAnchorHotelId)

  useConditionalEffect(
    async () =>
      import(
        /* webpackChunkName: "LanguageMismatchOverlay" */
        /* webpackPrefetch: true */
        'components/LanguageMismatchOverlay/LanguageMismatchOverlay'
      ),
    isSeoSRP
  )

  const isHydrated = useIsHydrated()
  const shouldInitializeCallToBook =
    isHydrated &&
    toggle(
      '27d5ef4a-call-to-book-anchor',
      false,
      Boolean(anchorHotelId && hotelId === anchorHotelId)
    )

  return (
    <>
      {isMobile && (
        <>
          <AllFilters />
          <MapOverlay mapComponent={<Map />} />
          <SortByMobile />
          {search.hasMap && isHydrated && <ShowMapFloatingButton />}
        </>
      )}
      {!isMobile && (
        <AllOffersOverlay variant="popup" sourceComponent="DesktopOffersTab" />
      )}
      <SearchWrapper data-id={searchStateDataId}>
        {retention.hasExitPopup && (
          <OfferViewContext.Provider value={OfferViewType.SRP_BEST_DEAL_MODAL}>
            {isHydrated && <ExitPopupSearchPage />}
          </OfferViewContext.Provider>
        )}

        {/* Added as part of 27d5ef4a-call-to-book-anchor */}
        {shouldInitializeCallToBook && (
          <InitializeCallToBook hotelId={hotelId} />
        )}

        <SearchBoxShadow>
          <SearchLayoutContainer
            listComponent={<Search />}
            mapComponent={<Map />}
          />
          {isDesktop && <AvailabilityPopupWrapper />}
          {!isMobile && isHydrated && <CookieBanner />}
          {isSeoSRP && isHydrated && <LanguageMismatchOverlay />}
        </SearchBoxShadow>
      </SearchWrapper>
    </>
  )
}

const MemoSearchView = React.memo(SearchView)

export const SearchViewContainer = () => {
  useInitSearch()
  useHandleUserGeolocation()
  useRedirectToHomeIfGeolocationFails()
  // Added as part of feature-srp-refresh-price
  useAutoRefreshStaleSearches()

  return <MemoSearchView />
}
